import type { TranslationKey } from '@/lib/i18n';

/**
 * One column of a table's registry. The registry is an array: its position
 * is the column order of a table nobody has configured.
 */
export interface ColumnDef {
  /** Stable key stored in the preferences. */
  key: string;
  /** i18n key of the column header and of its entry in the Colonnes menu. */
  label: TranslationKey;
  /** Shown when neither the company nor the user chose columns. */
  defaultVisible: boolean;
  /** Always shown: the menu cannot hide it and no stored value removes it. */
  required?: boolean;
  /** CSS width of the column. */
  width?: string;
}

/** The value stored under `table.<tableId>.columns`: the visible column keys, in order. */
export interface ColumnPreference {
  columns: string[];
}

/** The key under which a table's columns are stored. */
export function tableColumnsKey(tableId: string): string {
  return `table.${tableId}.columns`;
}

/** The keys of a stored value, or null when it holds none (absent, malformed or empty). */
function storedKeys(value: unknown): string[] | null {
  if (typeof value !== 'object' || value === null) return null;
  const columns = (value as { columns?: unknown }).columns;
  if (!Array.isArray(columns)) return null;
  const keys = columns.filter((key): key is string => typeof key === 'string');
  return keys.length > 0 ? keys : null;
}

/** `keys` without the ones outside `known`, and without repeats. */
function knownKeys(keys: readonly string[], known: ReadonlySet<string>): string[] {
  return [...new Set(keys)].filter((key) => known.has(key));
}

/**
 * Puts `key` into `visible` at its registry position: first when no column
 * before it in the registry is shown, last when none after it is, else right
 * after the nearest preceding registry column shown.
 */
export function insertInRegistryOrder(
  registry: readonly ColumnDef[],
  visible: readonly string[],
  key: string
): string[] {
  if (visible.includes(key)) return [...visible];
  const index = registry.findIndex((column) => column.key === key);
  if (index < 0) return [...visible];
  let after = -1;
  for (let i = index - 1; i >= 0 && after < 0; i -= 1) {
    after = visible.indexOf(registry[i]!.key);
  }
  const followed = registry.slice(index + 1).some((column) => visible.includes(column.key));
  const next = [...visible];
  if (after < 0) next.unshift(key);
  else if (!followed) next.push(key);
  else next.splice(after + 1, 0, key);
  return next;
}

/**
 * The columns a table may show: all of the registry when the company set no
 * default, else the company's columns plus the required ones.
 */
export function allowedColumnKeys(registry: readonly ColumnDef[], company: unknown): string[] {
  const all = new Set(registry.map((column) => column.key));
  const chosen = storedKeys(company);
  if (chosen === null) return registry.map((column) => column.key);
  const allowed = new Set(knownKeys(chosen, all));
  for (const column of registry) if (column.required) allowed.add(column.key);
  return registry.filter((column) => allowed.has(column.key)).map((column) => column.key);
}

/**
 * The visible column keys, in order. The company default narrows what is
 * allowed and is what shows when the user chose nothing; the user's choice
 * applies within the allowed columns. Unknown keys are dropped and required
 * columns are always shown.
 */
export function resolveColumnKeys(
  registry: readonly ColumnDef[],
  company: unknown,
  user: unknown
): string[] {
  const allowed = allowedColumnKeys(registry, company);
  const allowedSet = new Set(allowed);
  const companyKeys = storedKeys(company);
  const userKeys = storedKeys(user);

  let visible: string[];
  if (userKeys !== null) visible = knownKeys(userKeys, allowedSet);
  else if (companyKeys !== null) visible = knownKeys(companyKeys, allowedSet);
  else visible = registry.filter((column) => column.defaultVisible).map((column) => column.key);

  for (const column of registry) {
    if (column.required && !visible.includes(column.key)) {
      visible = insertInRegistryOrder(registry, visible, column.key);
    }
  }
  return visible;
}

/** One line of the Colonnes menu. */
export interface ColumnMenuItem {
  key: string;
  label: TranslationKey;
  visible: boolean;
  required: boolean;
}

/** The menu lines: the visible columns in order, then the allowed hidden ones in registry order. */
export function menuItems(
  registry: readonly ColumnDef[],
  allowed: readonly string[],
  visible: readonly string[]
): ColumnMenuItem[] {
  const byKey = new Map(registry.map((column) => [column.key, column]));
  const hidden = allowed.filter((key) => !visible.includes(key));
  return [...visible, ...hidden].flatMap((key) => {
    const column = byKey.get(key);
    return column
      ? [{ key, label: column.label, visible: visible.includes(key), required: column.required === true }]
      : [];
  });
}

/** `visible` with `key` shown or hidden; a required column is never hidden. */
export function toggleColumn(
  registry: readonly ColumnDef[],
  visible: readonly string[],
  key: string
): string[] {
  if (visible.includes(key)) {
    const column = registry.find((entry) => entry.key === key);
    return column?.required ? [...visible] : visible.filter((entry) => entry !== key);
  }
  return insertInRegistryOrder(registry, visible, key);
}

/** `visible` with `key` moved one place up (-1) or down (+1); unchanged at an edge. */
export function moveColumn(visible: readonly string[], key: string, direction: -1 | 1): string[] {
  const from = visible.indexOf(key);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= visible.length) return [...visible];
  const next = [...visible];
  [next[from], next[to]] = [next[to]!, next[from]!];
  return next;
}
